/**
 * Tenant list — the operator's inventory of every hosted tenant.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C_OPS.
 *
 * The one admin-ops page that needs no `reason` field: `GET /v1/admin/tenants`
 * names no single tenant (see `admin_ops.py`'s module docstring — "not
 * 'access to tenant data' in the sense U-5 means"), so nothing here prompts
 * for a justification. Every other admin-ops page does.
 *
 * This page never renders anything beyond `org_id`/`slug`/`display_name`/
 * `status`/`created_at` — the exact fields `admin_ops.list_tenants` returns.
 * There is no customer content, vault material, or receipt body reachable
 * from this component, because there is none in the response it reads.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { listTenants, provisionTenant } from './adminOpsApi';

const STATUS_LABELS = {
  active: 'Active',
  suspended: 'Suspended',
  closed: 'Closed',
};

export default function TenantListPage({ onSelectTenant }) {
  const [tenants, setTenants] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [ownerPrincipalId, setOwnerPrincipalId] = useState('');
  const [reason, setReason] = useState('');

  const refresh = useCallback(async () => {
    try {
      const payload = await listTenants(statusFilter ? { status: statusFilter } : {});
      setTenants((payload && payload.tenants) || []);
    } catch (failure) {
      setError(failure.message);
    }
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submitProvision = useCallback(
    async (event) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const payload = await provisionTenant({
          slug,
          displayName,
          ownerPrincipalId,
          reason,
        });
        setNotice(`Tenant ${payload.tenant.slug} provisioned (${payload.tenant.org_id}).`);
        setSlug('');
        setDisplayName('');
        setOwnerPrincipalId('');
        setReason('');
        await refresh();
      } catch (failure) {
        setError(failure.message);
      } finally {
        setBusy(false);
      }
    },
    [slug, displayName, ownerPrincipalId, reason, refresh],
  );

  return (
    <section className="admin-ops-tenant-list">
      <header>
        <h1>Tenants</h1>
        <p className="admin-ops-intro">
          Provisioning, support access, and usage metering for the hosted business. Every action
          against a specific tenant on this surface requires a reason, and every one is recorded
          in that tenant&apos;s audit trail.
        </p>
      </header>

      {error ? (
        <p className="admin-ops-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="admin-ops-notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="admin-ops-filter">
        <label htmlFor="admin-ops-status-filter">Status</label>
        <select
          id="admin-ops-status-filter"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {tenants.length === 0 ? (
        <p className="admin-ops-empty">No tenants match this filter.</p>
      ) : (
        <table className="admin-ops-tenant-table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Display name</th>
              <th>Status</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.org_id} data-status={tenant.status}>
                <td>{tenant.slug}</td>
                <td>{tenant.display_name}</td>
                <td>{STATUS_LABELS[tenant.status] || tenant.status}</td>
                <td>{tenant.created_at}</td>
                <td>
                  <button type="button" onClick={() => onSelectTenant && onSelectTenant(tenant.org_id)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="admin-ops-provision-form" onSubmit={submitProvision}>
        <h2>Provision a tenant</h2>

        <label htmlFor="admin-ops-slug">Slug</label>
        <input
          id="admin-ops-slug"
          type="text"
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
        />

        <label htmlFor="admin-ops-display-name">Display name</label>
        <input
          id="admin-ops-display-name"
          type="text"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <label htmlFor="admin-ops-owner">Owner principal id</label>
        <input
          id="admin-ops-owner"
          type="text"
          value={ownerPrincipalId}
          onChange={(event) => setOwnerPrincipalId(event.target.value)}
        />

        <label htmlFor="admin-ops-provision-reason">Reason (recorded in the audit trail)</label>
        <input
          id="admin-ops-provision-reason"
          type="text"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        <button type="submit" disabled={busy || !slug || !displayName || !ownerPrincipalId || !reason}>
          Provision
        </button>
      </form>
    </section>
  );
}
